import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { colors } from '@mobi/component-library/Theme/Common'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { MainContainer } from '@core/Components/Containers'
import { useAppSelector } from '@core/Store/hooks'
import { useAuthenticatedAccount } from '@core/Components/RequireAuth/hooks'
import { RunnerList } from '@core/Areas/Blackbook/Components'
import { showPushAlertPrompt } from '@core/Areas/Blackbook/helpers'
import { BlackbookSettings } from '@core/Areas/Blackbook/Components/BlackbookSettings/BlackbookSettings'
import { useBlackbookDetailsQuery } from './Hooks/useBlackbookDetailsQuery'
import { BlackbookAlertPromptTray } from './Components/BlackbookAlertPromptTray'
import { useTakeABreak } from '@core/Areas/Account/TakeABreak/Hooks'
import { isPushNotificationAvailable } from '@core/Utils/notifications/pushNotificationAlerts'

export const Blackbook: React.FC = () => {
  const { accountNumber } = useAuthenticatedAccount()
  const { data } = useTakeABreak()
  const isTakeABreakActive = data?.inProgress ?? false
  const [settingsOpen, setSettingsOpen] = useState(false)

  const { detailsQuery, pushAlertQuery, isLoading, isDetailsError, isError } =
    useBlackbookDetailsQuery(accountNumber)

  const {
    lastPushAlertPrompt: lastNotificationPrompt,
    pushAlertPromptCount: notificationPromptCount,
  } = useAppSelector(state => state.blackbook)

  const notificationStatusIsLoading =
    pushAlertQuery.isLoading ||
    detailsQuery.isLoading ||
    isLoading ||
    pushAlertQuery.data === undefined
  const isPushNotificationDisabled = !(pushAlertQuery.data ?? false)
  const isSmsEnabled = detailsQuery.data?.SendSms ?? false

  const shouldPromptPushNotification =
    !notificationStatusIsLoading &&
    !isTakeABreakActive &&
    !isError &&
    isPushNotificationAvailable &&
    isSmsEnabled &&
    isPushNotificationDisabled &&
    showPushAlertPrompt(lastNotificationPrompt, notificationPromptCount) &&
    !settingsOpen

  return (
    <>
      <MainContainer data-testid='blackbook-page' background='grey'>
        <PageHeader
          title='Blackbook'
          rightAlignedComponent={
            <Button
              size='sm'
              isIconOnlyButton
              color='tertiary_dark'
              disabled={pushAlertQuery.isLoading}
              onClick={() => setSettingsOpen(curr => !curr)}
            >
              <Icon name={settingsOpen ? 'SolidBell01' : 'LineBell01'} color={colors.white} />
            </Button>
          }
        />

        <RunnerList
          isError={isDetailsError}
          isLoading={isLoading}
          entries={detailsQuery.data?.Entries}
        />
      </MainContainer>

      <BlackbookAlertPromptTray
        accountNumber={accountNumber}
        showPushNotificationPrompt={shouldPromptPushNotification}
        sendEmail={detailsQuery.data?.SendEmail}
      />

      {createPortal(
        <Tray
          isOpen={settingsOpen}
          headerTitle='Blackbook Settings'
          onDismiss={() => setSettingsOpen(false)}
        >
          <BlackbookSettings isTakeABreakActive={isTakeABreakActive} />
        </Tray>,
        document.body
      )}
    </>
  )
}
